import { useState, useEffect } from 'react';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Building2, 
  Microscope, 
  Pill, 
  ClipboardCheck, 
  ShieldCheck, 
  FileText, 
  Lightbulb,
  Stethoscope,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import LoadingScreen from "@/components/LoadingScreen";
import { useLanguage } from '@/contexts/LanguageContext';
import { Helmet } from 'react-helmet-async';

const Services = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const { language, t } = useLanguage();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  const services = [
    {
      id: 1,
      title: t('services.card1.title'),
      description: t('services.card1.description'),
      icon: Building2,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      details: [
        t('services.card1.detail1'),
        t('services.card1.detail2'),
        t('services.card1.detail3'),
        t('services.card1.detail4'),
        t('services.card1.detail5'),
      ]
    },
    {
      id: 2,
      title: t('services.card2.title'),
      description: t('services.card2.description'),
      icon: Pill,
      color: "text-green-600",
      bgColor: "bg-green-100",
      details: [
        t('services.card2.detail1'),
        t('services.card2.detail2'),
        t('services.card2.detail3'),
        t('services.card2.detail4'),
        t('services.card2.detail5'),
      ]
    },
    {
      id: 3,
      title: t('services.card3.title'),
      description: t('services.card3.description'),
      icon: ClipboardCheck,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
      details: [
        t('services.card3.detail1'),
        t('services.card3.detail2'),
        t('services.card3.detail3'),
        t('services.card3.detail4'),
        t('services.card3.detail5'),
      ]
    },
    {
      id: 4,
      title: t('services.card4.title'),
      description: t('services.card4.description'),
      icon: ShieldCheck,
      color: "text-red-600",
      bgColor: "bg-red-100",
      details: [
        t('services.card4.detail1'),
        t('services.card4.detail2'),
        t('services.card4.detail3'),
        t('services.card4.detail4'),
        t('services.card4.detail5'),
      ]
    },
    {
      id: 5,
      title: t('services.card5.title'),
      description: t('services.card5.description'),
      icon: FileText,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
      details: [
        t('services.card5.detail1'),
        t('services.card5.detail2'),
        t('services.card5.detail3'),
        t('services.card5.detail4'),
        t('services.card5.detail5'),
      ]
    },
    {
      id: 6,
      title: t('services.card6.title'),
      description: t('services.card6.description'),
      icon: Lightbulb,
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
      details: [
        t('services.card6.detail1'),
        t('services.card6.detail2'),
        t('services.card6.detail3'),
        t('services.card6.detail4'),
        t('services.card6.detail5'),
      ]
    },
    {
      id: 7,
      title: t('services.card7.title'),
      description: t('services.card7.description'),
      icon: Stethoscope,
      color: "text-indigo-600",
      bgColor: "bg-indigo-100",
      details: [
        t('services.card7.detail1'),
        t('services.card7.detail2'),
        t('services.card7.detail3'),
        t('services.card7.detail4'),
        t('services.card7.detail5'),
      ]
    }
  ];

  const toggleService = (id: number) => {
    setExpandedService(expandedService === id ? null : id);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{language === 'en' ? 'Our Services | LeaderProMed Mauritania' : 'Nos Services | LeaderProMed Mauritanie'}</title>
        <meta name="description" content={language === 'en' ? 'Discover all our medical and pharmaceutical services in Mauritania: laboratory representation, drug promotion, registration, pharmacovigilance, tenders, investment consulting, and medical equipment.' : 'Découvrez tous nos services médicaux et pharmaceutiques en Mauritanie : représentation de laboratoires, promotion de médicaments, enregistrement, pharmacovigilance, appels d’offres, consulting investissement, équipement médical.'} />
        <meta name="keywords" content={language === 'en' ? 'services, pharmaceutical registration, drug registration, regulatory affairs, market access, Mauritania, Africa, international laboratory, medical, LeaderProMed, clinical trials, licensing, distributor, healthcare, B2B, partnership, consulting, pharmacovigilance, tenders, investment' : 'services, enregistrement pharmaceutique, enregistrement médicament, affaires réglementaires, accès au marché, Mauritanie, Afrique, laboratoire international, médical, LeaderProMed, essais cliniques, licence, distributeur, santé, B2B, partenariat, consulting, pharmacovigilance, appels d’offres, investissement'} />
        <meta property="og:title" content={language === 'en' ? 'Our Services | LeaderProMed Mauritania' : 'Nos Services | LeaderProMed Mauritanie'} />
        <meta property="og:description" content={language === 'en' ? 'Discover all our medical and pharmaceutical services in Mauritania: laboratory representation, drug promotion, registration, pharmacovigilance, tenders, investment consulting, and medical equipment.' : 'Découvrez tous nos services médicaux et pharmaceutiques en Mauritanie : représentation de laboratoires, promotion de médicaments, enregistrement, pharmacovigilance, appels d’offres, consulting investissement, équipement médical.'} />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="relative flex items-center justify-center bg-cover bg-center min-h-[40vh]"
        style={{ backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/background.jpg')" }}>
        <div className="text-center px-4 z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('services.title')}</h1>
          <p className="text-xl text-white max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <Card 
              key={service.id} 
              className={`bg-white shadow-lg transition-all duration-300 ${
                expandedService === service.id ? 'md:col-span-2 lg:col-span-3' : ''
              }`}
            >
              <CardHeader className="text-center pb-2">
                <div className={`mx-auto ${service.bgColor} p-4 rounded-full mb-4`}>
                  <service.icon size={36} className={service.color} />
                </div>
                <CardTitle className="text-xl">{service.title}</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-gray-600">
                  {service.description}
                </CardDescription>
                {expandedService === service.id && (
                  <div className="mt-6 text-left">
                    <h4 className="font-semibold text-gray-800 mb-4">{t('services.detailsTitle')}</h4>
                    <ul className="space-y-3">
                      {service.details.map((detail, index) => (
                        <li key={index} className="flex items-start">
                          <div className={`${service.color} mr-2 mt-1`}>•</div>
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
              <CardFooter className="pt-2 justify-center">
                <Button 
                  variant="outline" 
                  className={`${service.color} border-current hover:bg-opacity-10 hover:bg-current flex items-center gap-2`}
                  onClick={() => toggleService(service.id)}
                >
                  {expandedService === service.id ? (
                    <>
                      {t('services.seeLess')} <ChevronUp size={16} />
                    </>
                  ) : (
                    <>
                      {t('services.learnMore')} <ChevronDown size={16} />
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-blue-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-blue-600 mb-4">{t('services.whyChooseUsTitle')}</h2>
            <div className="h-1 w-20 bg-blue-600 mb-4 mx-auto"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('services.whyChooseUsDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <Microscope className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">{t('services.expertiseLocaleTitle')}</h3>
                <p className="text-gray-600">
                  {t('services.expertiseLocaleDescription')}
                </p>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <Building2 className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">{t('services.extendedNetworkTitle')}</h3>
                <p className="text-gray-600">
                  {t('services.extendedNetworkDescription')}
                </p>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <ShieldCheck className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">{t('services.qualityGuaranteeTitle')}</h3>
                <p className="text-gray-600">
                  {t('services.qualityGuaranteeDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
